// The apps' offline notice (PMU-270): offline since when, that screens show what was last loaded,
// how many changes wait to be sent, and any that the server refused once it was back.
import { useEffect, useState, useSyncExternalStore } from "react";
import { Banner, Button } from "@vavr/ui";
import { dismissFailed, flushQueue, offlineEnabled, offlineState, subscribeOffline, type OfflineState } from "./offline.ts";

export function useOffline(): OfflineState {
  return useSyncExternalStore(subscribeOffline, offlineState, offlineState);
}

const clock = (at: number) => new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/**
 * `lan`: the staff app's links to the school's door tablets on the Wi-Fi (offline phase 2), for
 * phones that have them; with the internet down they are how door requests still arrive.
 */
export function OfflineBanner({ lan }: { lan?: { doors: number } } = {}) {
  const s = useOffline();
  const [, tick] = useState(0);
  useEffect(() => { const i = setInterval(() => tick((n) => n + 1), 30_000); return () => clearInterval(i); }, []);
  if (!offlineEnabled()) return null;
  const waiting = s.queued ? ` ${s.queued} ${s.queued === 1 ? "change waits" : "changes wait"} to be sent.` : "";
  return (
    <>
      {!s.online && (
        <Banner tone="warning" title={`Offline since ${clock(s.since ?? Date.now())}`}>
          You can keep working: screens show what was last loaded, and what you do here is saved on this device and sent when the internet is back.{waiting}
          {lan && (
            <span className="pmu-offline__lan" data-linked={lan.doors > 0}>
              {" "}{lan.doors > 0
                ? `Linked to ${lan.doors === 1 ? "the door" : `${lan.doors} doors`} on the school Wi-Fi: parents' requests at the door still reach you.`
                : "Not linked to a door on the Wi-Fi: requests from the door reach you when the internet is back."}
            </span>
          )}
        </Banner>
      )}
      {s.online && s.queued > 0 && <Banner tone="info" title="Sending what you did offline">{waiting.trim()}</Banner>}
      {s.failed.length > 0 && (
        <Banner tone="danger" title={s.failed.length === 1 ? "One offline change was not accepted" : `${s.failed.length} offline changes were not accepted`}
          actions={<Button size="sm" variant="secondary" onClick={() => { void dismissFailed(); void flushQueue(); }}>OK</Button>}>
          {s.failed.map((f) => `${f.label} (${clock(f.at)}): ${f.message}`).join(" · ")}
        </Banner>
      )}
    </>
  );
}
